import {
    Wrench,
    FileText,
    Clock,
    ShoppingCart,
    KeyRound,
    Ticket,
    TrendingUp,
    ArrowUpRight,
} from "lucide-react";

/**
 * DashboardMockup — SVG-like SaaS dashboard preview used in the hero.
 * Fully custom, no external images.
 */
export default function DashboardMockup() {
    const cards = [
        {
            icon: Wrench,
            label: "Interventi oggi",
            value: "14",
            delta: "+3 rispetto a ieri",
            tone: "cyan",
        },
        {
            icon: FileText,
            label: "Contratti in scadenza",
            value: "7",
            delta: "prossimi 30 gg",
            tone: "amber",
        },
        {
            icon: Clock,
            label: "Ore residue",
            value: "312h",
            delta: "su 480h attive",
            tone: "teal",
        },
        {
            icon: ShoppingCart,
            label: "Preventivi aperti",
            value: "22",
            delta: "€ 148.500 potenziali",
            tone: "cyan",
        },
        {
            icon: KeyRound,
            label: "Licenze attive",
            value: "1.284",
            delta: "M365 · Sophos · Nebula",
            tone: "indigo",
        },
        {
            icon: Ticket,
            label: "Ticket da gestire",
            value: "9",
            delta: "2 in ritardo",
            tone: "rose",
        },
    ];

    const toneMap = {
        cyan: "text-cyan-300 bg-cyan-500/10 ring-cyan-400/20",
        teal: "text-teal-300 bg-teal-500/10 ring-teal-400/20",
        amber: "text-amber-300 bg-amber-500/10 ring-amber-400/20",
        indigo: "text-indigo-300 bg-indigo-500/10 ring-indigo-400/20",
        rose: "text-rose-300 bg-rose-500/10 ring-rose-400/20",
    };

    return (
        <div
            data-testid="hero-dashboard-mockup"
            className="relative w-full rounded-2xl bg-gradient-to-br from-slate-900/90 to-slate-950/95 ring-1 ring-white/10 shadow-[0_30px_80px_-20px_rgba(6,182,212,0.35)] overflow-hidden"
        >
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                <div className="ml-3 flex-1 rounded-md bg-white/[0.04] ring-1 ring-white/5 px-3 py-1 text-[11px] text-slate-400 font-mono">
                    app.tecsuite.it / dashboard
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-400 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                    Live
                </span>
            </div>

            <div className="p-4 sm:p-5">
                {/* Top bar */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                            Panoramica
                        </p>
                        <p className="text-white font-display font-semibold text-lg">
                            Buongiorno, Marco
                        </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-400">
                        <span className="px-2 py-1 rounded-md bg-white/5 ring-1 ring-white/10">
                            Oggi
                        </span>
                        <span className="px-2 py-1 rounded-md bg-white/5 ring-1 ring-white/10">
                            Settimana
                        </span>
                        <span className="px-2 py-1 rounded-md bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/30">
                            Mese
                        </span>
                    </div>
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
                    {cards.map((c, i) => (
                        <div
                            key={c.label}
                            className="group relative rounded-xl bg-white/[0.03] ring-1 ring-white/5 p-3 sm:p-4 hover:ring-white/15 transition"
                            style={{ animation: `bar-grow 0.9s ${i * 0.05}s both` }}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div
                                    className={`inline-flex items-center justify-center h-7 w-7 rounded-md ring-1 ${toneMap[c.tone]}`}
                                >
                                    <c.icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                                </div>
                                <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-300 transition" />
                            </div>
                            <p className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold leading-tight">
                                {c.label}
                            </p>
                            <p className="mt-1 text-xl sm:text-2xl font-display font-bold text-white leading-none">
                                {c.value}
                            </p>
                            <p className="mt-1.5 text-[11px] text-slate-500">{c.delta}</p>
                        </div>
                    ))}
                </div>

                {/* Mini chart */}
                <div className="mt-4 rounded-xl bg-white/[0.03] ring-1 ring-white/5 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-teal-300" />
                            <p className="text-xs font-semibold text-white">
                                Saturazione tecnici — ultime 12 settimane
                            </p>
                        </div>
                        <p className="text-[11px] text-slate-400">
                            <span className="text-teal-300 font-semibold">+8%</span>{" "}
                            trend positivo
                        </p>
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                        {[38, 52, 44, 60, 48, 66, 58, 72, 64, 78, 70, 84].map((h, i) => (
                            <div
                                key={i}
                                className="flex-1 rounded-t-md bg-gradient-to-t from-cyan-500/70 to-teal-300/80 bar-anim"
                                style={{
                                    height: `${h}%`,
                                    animationDelay: `${i * 0.06}s`,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -left-3 sm:-left-6 bottom-6 hidden md:block">
                <div className="rounded-xl bg-white/95 backdrop-blur ring-1 ring-slate-200 shadow-xl px-3 py-2.5 flex items-center gap-2.5">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-600 ring-1 ring-teal-200">
                        <FileText className="w-4 h-4" />
                    </span>
                    <div>
                        <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                            Rapportino generato
                        </p>
                        <p className="text-xs font-semibold text-slate-900">
                            #2025-1284 · PDF pronto
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
